/** Default compression quality (0-100) */
export const DEFAULT_COMPRESSION_QUALITY = 85;

/** File size in bytes: 1MB */
export const FILE_SIZE_1_MB = 1048576;

/** Default max file size: 10MB */
export const DEFAULT_MAX_FILE_SIZE = 10 * FILE_SIZE_1_MB;

/** Debounce delay for compression in ms */
export const COMPRESSION_DEBOUNCE_MS = 300;

/** Accepted image types */
export const IMAGE_FILE_ACCEPTED = "image/*";

/** Supported output formats */
export const OUTPUT_FORMATS = {
  WEBP: "image/webp" as const,
  JPEG: "image/jpeg" as const,
};

/** Styles for the image upload component */
export const IMAGE_UPLOAD_STYLES = {
  container: "w-full",
  dropzone:
    "border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-gray-400 transition-colors",
  dropzoneActive: "border-blue-500 bg-blue-50",
  dropzoneDisabled: "cursor-not-allowed opacity-50 hover:border-gray-300",
  preview: "relative w-full rounded-lg overflow-hidden",
  previewImage: "w-full h-32 object-cover",
  removeButton:
    "absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow-sm transition-colors",
  uploadIcon: "mx-auto h-8 w-8 text-gray-400",
  uploadText: "mt-2 text-sm text-gray-600",
  uploadHint: "text-xs text-gray-400",
  errorText: "mt-1 text-xs text-red-500",
  loadingOverlay:
    "absolute inset-0 bg-white/80 flex items-center justify-center",
} as const;

/**
 * Compress a blob to the specified format and quality
 */
export async function compressBlob(
  source: Blob,
  mimeType: "image/webp" | "image/jpeg",
  quality: number
): Promise<Blob> {
  const bitmap = await createImageBitmap(source);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    throw new Error("Failed to get canvas context");
  }
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to compress image"));
        }
      },
      mimeType,
      quality / 100
    );
  });
}

/**
 * Convert a blob to a data URL
 */
export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read blob"));
    reader.readAsDataURL(blob);
  });
}

/**
 * Format bytes to human-readable string
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < FILE_SIZE_1_MB) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / FILE_SIZE_1_MB).toFixed(2)} MB`;
}

/**
 * Calculate compression savings percentage
 */
export function calculateSavings(original: number, compressed: number): number {
  if (original === 0) return 0;
  return Math.round(((original - compressed) / original) * 100);
}
